import {
  Box,
  Paper,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AccessTime from "@mui/icons-material/AccessTime";
import {
  sortSelector,
  changeSortType,
  ISortTypes,
  sortTypeMap,
} from "redux/sort";
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import { useTranslations } from "hooks/useTranslations";
import { ILightItinerary } from "redux/filters";
import { useCountUp } from "use-count-up";
import {
  pushToDataLayer,
  minutesTimeSpan,
  formatPrice,
  useConfig,
} from "@thekilroygroup/shared-ui";

interface IProps {
  currency: string;
  results: {
    best: ILightItinerary;
    cheapest: ILightItinerary;
    fastest: ILightItinerary;
  };
}
export function SortSearchResults(props: IProps) {
  const {
    currency,
    results,
    results: { best, cheapest, fastest },
  } = props;
  const { translations } = useTranslations();
  const { locale } = useConfig();

  const { sortType, filteredIndexes } = useSelector(sortSelector);
  const dispatch = UseAppDispatch();

  const handleChange = (value: ISortTypes) => {
    pushToDataLayer(value, "Sort");
    dispatch(changeSortType(value));
  };
  const [bestPrice, setBestPrice] = useState(0);
  const [bestDuration, setBestDuration] = useState(0);
  const [cheapestPrice, setCheapestPrice] = useState(0);
  const [cheapestDuration, setCheapestDuration] = useState(0);
  const [fastestPrice, setFastestPrice] = useState(0);
  const [fastestDuration, setFastestDuration] = useState(0);

  useEffect(() => {
    resetPenaltyPointsPrice();
    resetPricePrice();
    resetFlightTimePrice();
    resetPenaltyPointsDuration();
    resetPriceDuration();
    resetFlightTimeDuration();
  }, [results]);

  const { value: penaltyPointsValuePrice, reset: resetPenaltyPointsPrice } =
    useCountUp({
      isCounting: true,
      start: bestPrice,
      end: best.price,
      duration: 0.5,
      easing: "linear",
      decimalPlaces: 0,
      onComplete: () => {
        setBestPrice(best.price);
      },
    });
  const { value: priceValuePrice, reset: resetPricePrice } = useCountUp({
    isCounting: true,
    start: cheapestPrice,
    end: cheapest.price,
    duration: 0.5,
    easing: "linear",
    decimalPlaces: 0,
    onComplete: () => {
      setCheapestPrice(cheapest.price);
    },
  });
  const { value: flightTimeValuePrice, reset: resetFlightTimePrice } =
    useCountUp({
      isCounting: true,
      start: fastestPrice,
      end: fastest.price,
      duration: 0.5,
      easing: "linear",
      decimalPlaces: 0,
      onComplete: () => {
        setFastestPrice(fastest.price);
      },
    });

  const { value: penaltyPointsValueTime, reset: resetPenaltyPointsDuration } =
    useCountUp({
      isCounting: true,
      start: bestDuration,
      end: best.duration[0],
      duration: 0.5,
      easing: "linear",
      decimalPlaces: 0,
      onComplete: () => {
        setBestDuration(best.duration[0]);
      },
    });
  const { value: priceValueTime, reset: resetPriceDuration } = useCountUp({
    isCounting: true,
    start: cheapestDuration,
    end: cheapest.duration[0],
    duration: 0.5,
    easing: "linear",
    decimalPlaces: 0,
    onComplete: () => {
      setCheapestDuration(cheapest.duration[0]);
    },
  });
  const { value: flightTimeValueTime, reset: resetFlightTimeDuration } =
    useCountUp({
      isCounting: true,
      start: fastestDuration,
      end: fastest.duration[0],
      duration: 0.5,
      easing: "linear",
      decimalPlaces: 0,
      onComplete: () => {
        setFastestDuration(fastest.duration[0]);
      },
    });

  const tabs: ISortTypes[] = ["penaltyPoints", "price", "flightTime"];

  const prices = {
    penaltyPoints: penaltyPointsValuePrice,
    price: priceValuePrice,
    flightTime: flightTimeValuePrice,
  };

  const durations = {
    penaltyPoints: penaltyPointsValueTime,
    price: priceValueTime,
    flightTime: flightTimeValueTime,
  };

  const {
    sortWrapper,
    tab,
    tabTitle,
    timeDetails,
    priceDetails,
    detailParagraph,
    details,
  } = sxStyles;
  return (
    <Stack sx={sortWrapper}>
      <Paper elevation={4}>
        <Tabs
          aria-label="tabs"
          value={sortType}
          TabIndicatorProps={{
            style: { display: "none" },
          }}
          onChange={(e, value: ISortTypes) => handleChange(value)}
        >
          {tabs.map((sortType: ISortTypes, i) => {
            return (
              <Tab
                sx={tab}
                value={sortType}
                key={`sort-tab-${i}`}
                label={
                  <Stack>
                    <Typography sx={tabTitle} variant="h5">
                      {translations.sort[sortTypeMap[sortType]]}
                    </Typography>
                    <Stack direction="row" alignItems="baseline">
                      <Box sx={{ ...timeDetails, ...details } as SxProps}>
                        <AccessTime fontSize="small" />
                        <Typography
                          variant="bodySmall"
                          sx={{ mr: 1, ...detailParagraph }}
                        >
                          {minutesTimeSpan(
                            filteredIndexes[sortType].length
                              ? (durations[sortType] as number)
                              : 0,
                            translations.timeLabels,
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ ...priceDetails, ...details } as SxProps}>
                        <Typography variant="bodySmall" sx={detailParagraph}>
                          {formatPrice(
                            filteredIndexes[sortType].length
                              ? prices[sortType]
                              : 0,
                            locale,
                            currency,
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
      </Paper>
    </Stack>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  sortWrapper: {
    display: "flex",
  },
  tab: {
    pr: 2,
    color: "black.main",
    border: `1px solid`,
    borderColor: "grey.200",
    textTransform: "none",
    "&.Mui-selected": {
      color: "white.main",
      backgroundColor: "primary.main",
      border: `1px solid`,
      borderColor: "primary.main",
    },
  },
  tabTitle: {
    display: "flex",
    alignItems: "center",
    mb: 0.5,
    lineHeight: 1,
    "& svg": {
      mr: 1,
    },
  },
  details: {
    display: "flex",
    minWidth: 9.5,
    alignItems: "baseLine",
  },
  detailParagraph: {
    lineHeight: 1,
    alignItems: "baseLine",
  },
  timeDetails: {
    mr: 0.5,
    "& svg": {
      transform: "translateY(5px)",
      mr: 0.5,
      "& p": {
        mr: 1,
      },
    },
  },
  currencySymbol: {
    textTransform: "uppercase",
  },
};
